import * as z from 'zod';
import axios from 'axios';
import client from '../client';

import { SEO, Localization } from '../strapiSchema';
import { blocksSchema } from '../blocksSchema';
import { blocks, seo, localizations } from '../strapiBlocks';

const PRIMARY_LOCALE = 'fr-FR';

const PageZod = z.object({
  id: z.number(),
  documentId: z.string(),
  slug: z.string(),
  headerColor: z.enum(['noir', 'gris']),
  blocks: z.array(blocksSchema),
  seo: SEO,
  localizations: z.array(Localization),
  locale: z.string(),
});

export type PageType = z.infer<typeof PageZod>;

const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const logMissingPage = (slug: string, locale: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[pages] Page "${slug}" not found for locale "${locale}", skipping.`);
};

const fetchPageFromApi = async (
  slug: string,
  locale: string,
): Promise<PageType | null> => {
  try {
    const response = await client.get('/api/pages', {
      params: {
        locale,
        populate: {
          blocks,
          seo,
          localizations,
        },
        filters: {
          slug: {
            $eq: slug,
          },
        },
      },
    });

    if (!response.data?.data?.length) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingPage(slug, locale);
      }
      return null;
    }

    return PageZod.parseAsync(response.data.data[0]);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingPage(slug, locale);
      return null;
    }

    throw error;
  }
};

const getPage = async (
  slug: string,
  locale: string = PRIMARY_LOCALE,
): Promise<PageType | null> => {
  try {
    const page = await fetchPageFromApi(slug, locale);

    if (!page) {
      if (locale !== PRIMARY_LOCALE) {
        return null;
      }
      throw new Error('No page found for this slug');
    }

    return page;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match Page structure : ${errorMessage}`);
  }
};

export {
  getPage,
};